// Phase 72.A/B — shared options + helpers for the Academic V2 surface.

export const STATUS_OPTIONS = ['Good', 'Needs Attention', 'At Risk', 'On Hold', 'Grade Completed', 'Graduated'];

export const SYSTEM_OPTIONS = ['Portuguese', 'American', 'British', 'University', 'Online', 'Language Course', 'Other'];

// Phase 75.1 — "Late - Justified" counts as attended but is rendered distinctly
// in the weekly breakdown so teachers can see justified-vs-unjustified tardies.
export const ATTENDANCE_STATUSES = ['Present', 'Late', 'Late - Justified', 'Absent', 'Excused', 'Online', 'No Show'];

export const NOTE_TYPES = ['General', 'Teacher Feedback', 'Concern', 'Progress Update', 'Document Note', 'Attendance Note'];

export const DOCUMENT_TYPES = [
  'School Report', 'Transcript', 'Certificate', 'Exam Result', 'Enrollment Proof',
  'Visa / Study Document', 'University Document', 'Parent Authorization',
  'Tutor Report', 'Other',
];

export const STATUS_COLORS = {
  Good: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'Needs Attention': 'bg-amber-100 text-amber-800 border-amber-200',
  'At Risk': 'bg-red-100 text-red-800 border-red-200',
  'On Hold': 'bg-gray-100 text-gray-800 border-gray-200',
  Graduated: 'bg-sky-100 text-sky-800 border-sky-200',
  'Grade Completed': 'bg-blue-100 text-blue-800 border-blue-200',
  good_standing: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  warning: 'bg-amber-100 text-amber-800 border-amber-200',
  probation: 'bg-red-100 text-red-800 border-red-200',
  excellent: 'bg-emerald-100 text-emerald-800 border-emerald-200',
};

export const profileLabel = (p) => p?.profile_name || p?.school_name || 'Untitled profile';

export const formatDate = (s) => {
  if (!s) return '—';
  // Phase 75.2 — when given a date-only ISO string (YYYY-MM-DD) we MUST
  // render the calendar date as-is. `new Date('2026-03-30')` parses as
  // UTC midnight which shifts back a day in west-of-UTC zones. Render
  // date-only inputs using locale formatting of the literal Y/M/D parts.
  if (typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, dd] = s.split('-').map(Number);
    const d = new Date(y, m - 1, dd, 12, 0, 0);
    return d.toLocaleDateString();
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString();
};

export const formatDateTime = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString();
};

// ─── Phase 75.2 — safe date-only helpers ───────────────────────────────
// Calendar dates (week_start_date, week_end_date, daily breakdown rows) MUST
// stay aligned to the user's selected day. `Date.toISOString()` always emits
// UTC, which shifts back a day in any zone west of UTC (e.g. Europe/Lisbon
// during DST: 30 Mar 2026 00:00 local = 29 Mar 2026 23:00 UTC). These
// helpers operate on local calendar components ONLY — never on UTC clocks.

// Format a Date instance (or YYYY-MM-DD string) as YYYY-MM-DD using LOCAL
// calendar components. Never converts to UTC.
export const toLocalISODate = (input) => {
  if (input === null || input === undefined || input === '') return '';
  if (typeof input === 'string') {
    // Already a date-only string → return verbatim.
    if (/^\d{4}-\d{2}-\d{2}$/.test(input)) return input;
    // Otherwise parse and fall through.
    input = new Date(input);
  }
  if (!(input instanceof Date) || Number.isNaN(input.getTime())) return '';
  const yyyy = input.getFullYear();
  const mm = String(input.getMonth() + 1).padStart(2, '0');
  const dd = String(input.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// Parse a YYYY-MM-DD string into a Date anchored at 12:00 LOCAL (noon).
// Noon avoids DST transition edges (which only ever shift the 02:00 hour).
export const parseLocalDate = (iso) => {
  if (!iso || typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    return null;
  }
  const [y, m, dd] = iso.split('-').map(Number);
  return new Date(y, m - 1, dd, 12, 0, 0);
};

// Add `n` calendar days to a YYYY-MM-DD string (local). Returns YYYY-MM-DD.
export const addDaysToISODate = (iso, n) => {
  const d = parseLocalDate(iso);
  if (!d) return iso;
  d.setDate(d.getDate() + n);
  return toLocalISODate(d);
};

// Local Monday for a YYYY-MM-DD string (or current local date if absent).
export const mondayOfISODate = (iso) => {
  const d = iso ? parseLocalDate(iso) : new Date();
  if (!d || Number.isNaN(d.getTime())) return '';
  // JS: 0=Sun, 1=Mon … 6=Sat. Convert so Monday=0, Sunday=6.
  const offset = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - offset);
  return toLocalISODate(d);
};

export const monday = (d = new Date()) => {
  const m = new Date(d);
  m.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return m;
};
